import { createFileRoute, useNavigate, Link } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "@/lib/auth-context";
import { supabase } from "@/integrations/supabase/client";
import { zodiacOf, ZODIAC_ORDER } from "@/lib/zodiac";
import { SiteHeader } from "@/components/site-header";
import { AdminTabs } from "@/components/admin-tabs";
import { Input } from "@/components/ui/input";
import {
  Ticket,
  LogIn,
  Coins,
  Wine,
  CreditCard,
  CalendarDays,
  Search,
  Crown,
} from "lucide-react";

export const Route = createFileRoute("/harikaradmin/members-report")({
  head: () => ({ meta: [{ title: "Üyeler Raporu — Admin" }] }),
  component: MembersReportPage,
});

interface ProfileLite {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  photo_url: string | null;
  birth_date?: string | null;
}

interface TicketRow {
  user_id: string;
  event_id: string;
  checked_in_at: string | null;
  order_id: string | null;
}

interface TxRow {
  user_id: string;
  amount: number;
  type: string;
}

interface VoucherRow {
  user_id: string;
  redeemed_at: string | null;
  drink_id: string;
  order_id: string | null;
}

interface OrderRow {
  id: string;
  user_id: string;
  total_kurus: number;
  status: string;
  event_id: string | null;
  payment_method: string | null;
}

async function fetchAll<T>(
  build: () => any,
  pageSize = 1000,
): Promise<T[]> {
  const all: T[] = [];
  let from = 0;
  // Loop until a page returns fewer rows than pageSize
  // eslint-disable-next-line no-constant-condition
  while (true) {
    const { data, error } = await build().range(from, from + pageSize - 1);
    if (error) throw error;
    const rows = (data as T[]) ?? [];
    all.push(...rows);
    if (rows.length < pageSize) break;
    from += pageSize;
  }
  return all;
}

function MembersReportPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();

  const [profiles, setProfiles] = useState<Record<string, ProfileLite>>({});
  const [tickets, setTickets] = useState<TicketRow[]>([]);
  const [txs, setTxs] = useState<TxRow[]>([]);
  const [vouchers, setVouchers] = useState<VoucherRow[]>([]);
  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [approvedCount, setApprovedCount] = useState(0);
  const [busy, setBusy] = useState(true);
  const [search, setSearch] = useState("");

  useEffect(() => {
    if (!loading && (!user || !isAdmin)) void navigate({ to: "/" });
  }, [loading, user, isAdmin, navigate]);

  const load = async () => {
    setBusy(true);
    const [pRes, pApprovedRes, allTickets, allTxs, allVouchers, allOrders] = await Promise.all([
      fetchAll<ProfileLite>(() =>
        supabase.from("profiles").select("id, first_name, last_name, email, photo_url, birth_date"),
      ),
      supabase
        .from("profiles")
        .select("id", { count: "exact", head: true })
        .eq("membership_status", "approved"),
      fetchAll<TicketRow>(() =>
        supabase.from("tickets").select("user_id, event_id, checked_in_at, order_id"),
      ),
      fetchAll<TxRow>(() =>
        supabase.from("point_transactions").select("user_id, amount, type"),
      ),
      fetchAll<VoucherRow>(() =>
        supabase.from("drink_vouchers").select("user_id, redeemed_at, drink_id, order_id"),
      ),
      fetchAll<OrderRow>(() =>
        supabase.from("orders").select("id, user_id, total_kurus, status, event_id, payment_method"),
      ),
    ]);

    // Build a set of order IDs that were admin-granted (comps) so we can
    // exclude their tickets/vouchers from "real" purchase stats.
    const grantOrderIds = new Set(
      allOrders.filter((o) => o.payment_method === "admin_grant").map((o) => o.id),
    );

    // Rapor dışı bırakılacak üyeler (kurucu / admin kendi hesabı vb.)
    const EXCLUDED_USER_IDS = new Set<string>([
      "5c4a883a-d8a5-4f1f-aa6e-8b2c731d4d3e", // Alican Bor
    ]);

    const pMap: Record<string, ProfileLite> = {};
    for (const p of pRes) {
      if (EXCLUDED_USER_IDS.has(p.id)) continue;
      pMap[p.id] = p;
    }
    setProfiles(pMap);
    setApprovedCount(pApprovedRes.count ?? 0);
    setTickets(
      allTickets.filter(
        (t) => !EXCLUDED_USER_IDS.has(t.user_id) && (!t.order_id || !grantOrderIds.has(t.order_id)),
      ),
    );
    setTxs(allTxs.filter((t) => !EXCLUDED_USER_IDS.has(t.user_id)));
    setVouchers(
      allVouchers.filter(
        (v) => !EXCLUDED_USER_IDS.has(v.user_id) && (!v.order_id || !grantOrderIds.has(v.order_id)),
      ),
    );
    setOrders(
      allOrders.filter(
        (o) => !EXCLUDED_USER_IDS.has(o.user_id) && o.payment_method !== "admin_grant",
      ),
    );
    setBusy(false);
  };

  useEffect(() => {
    if (user && isAdmin) void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, isAdmin]);

  const stats = useMemo(() => {
    const map = new Map<
      string,
      {
        tickets: number;
        checkIns: number;
        uniqueEvents: Set<string>;
        jSpent: number;
        jEarned: number;
        drinksRedeemed: number;
        drinksPurchased: number;
        tlSpent: number;
        tlOrders: number;
      }
    >();

    const ensure = (uid: string) => {
      if (!map.has(uid)) {
        map.set(uid, {
          tickets: 0,
          checkIns: 0,
          uniqueEvents: new Set(),
          jSpent: 0,
          jEarned: 0,
          drinksRedeemed: 0,
          drinksPurchased: 0,
          tlSpent: 0,
          tlOrders: 0,
        });
      }
      return map.get(uid)!;
    };

    for (const t of tickets) {
      const u = ensure(t.user_id);
      u.tickets += 1;
      u.uniqueEvents.add(t.event_id);
      if (t.checked_in_at) u.checkIns += 1;
    }

    for (const tx of txs) {
      const u = ensure(tx.user_id);
      if (tx.type === "spend" && tx.amount < 0) u.jSpent += -tx.amount;
      else if (tx.type === "earn" && tx.amount > 0) u.jEarned += tx.amount;
    }

    for (const v of vouchers) {
      const u = ensure(v.user_id);
      u.drinksPurchased += 1;
      if (v.redeemed_at) u.drinksRedeemed += 1;
    }

    for (const o of orders) {
      if (o.status !== "paid") continue;
      const u = ensure(o.user_id);
      u.tlSpent += o.total_kurus;
      u.tlOrders += 1;
    }

    const arr = Array.from(map.entries()).map(([user_id, v]) => ({
      user_id,
      ...v,
      eventCount: v.uniqueEvents.size,
    }));

    return {
      byTickets: [...arr].sort((a, b) => b.tickets - a.tickets).slice(0, 50),
      byCheckIns: [...arr].sort((a, b) => b.checkIns - a.checkIns).slice(0, 50),
      byEvents: [...arr].sort((a, b) => b.eventCount - a.eventCount).slice(0, 50),
      byJSpent: [...arr].sort((a, b) => b.jSpent - a.jSpent).slice(0, 50),
      byDrinks: [...arr].sort((a, b) => b.drinksRedeemed - a.drinksRedeemed).slice(0, 50),
      byTlSpent: [...arr].sort((a, b) => b.tlSpent - a.tlSpent).slice(0, 50),
      totalMembers: approvedCount,
    };
  }, [tickets, txs, vouchers, orders, profiles, approvedCount]);

  const filteredMembers = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return Object.values(profiles);
    return Object.values(profiles).filter((p) => {
      const name = `${p.first_name} ${p.last_name}`.toLowerCase();
      return name.includes(q) || p.email.toLowerCase().includes(q);
    });
  }, [profiles, search]);

  const fmtTL = (k: number) =>
    `${(k / 100).toLocaleString("tr-TR", { minimumFractionDigits: 2 })} ₺`;

  if (loading || !user || !isAdmin) {
    return (
      <div className="min-h-screen bg-background">
        <SiteHeader />
        <div className="mx-auto max-w-6xl px-4 py-10 text-center text-muted-foreground">
          Yükleniyor...
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <div className="mx-auto max-w-6xl px-4 py-10">
        <h1 className="mb-6 text-3xl font-bold tracking-tight">Admin</h1>
        <AdminTabs />

        <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
          <StatCard label="Onaylı üye" value={stats.totalMembers.toLocaleString("tr-TR")} icon={Crown} />
          <StatCard label="Toplam bilet" value={tickets.length.toLocaleString("tr-TR")} icon={Ticket} />
          <StatCard label="Toplam içecek" value={vouchers.length.toLocaleString("tr-TR")} icon={Wine} />
          <StatCard label="Toplam J işlemi" value={txs.length.toLocaleString("tr-TR")} icon={Coins} />
        </section>

        <ZodiacSection profiles={profiles} />

        <div className="mb-6">
          <Input
            placeholder="Üye ara (ad, soyad, e-posta)..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="max-w-sm"
          />
        </div>

        <div className="grid gap-6 lg:grid-cols-2">
          <RankingCard
            title="En çok bilet alanlar"
            icon={Ticket}
            rows={stats.byTickets}
            profiles={profiles}
            cols={[
              { label: "Bilet", key: "tickets" },
              { label: "Giriş", key: "checkIns" },
              { label: "Etkinlik", key: "eventCount" },
            ]}
          />
          <RankingCard
            title="En çok giriş yapanlar"
            icon={LogIn}
            rows={stats.byCheckIns}
            profiles={profiles}
            cols={[
              { label: "Giriş", key: "checkIns" },
              { label: "Bilet", key: "tickets" },
              { label: "Etkinlik", key: "eventCount" },
            ]}
          />
          <RankingCard
            title="En çok farklı etkinliğe gidenler"
            icon={CalendarDays}
            rows={stats.byEvents}
            profiles={profiles}
            cols={[
              { label: "Etkinlik", key: "eventCount" },
              { label: "Bilet", key: "tickets" },
              { label: "Giriş", key: "checkIns" },
            ]}
          />
          <RankingCard
            title="En çok J harcayanlar"
            icon={Coins}
            rows={stats.byJSpent}
            profiles={profiles}
            cols={[
              { label: "J Harcanan", key: "jSpent", suffix: " J" },
              { label: "J Kazanım", key: "jEarned", suffix: " J" },
            ]}
          />
          <RankingCard
            title="En çok içenler"
            icon={Wine}
            rows={stats.byDrinks}
            profiles={profiles}
            cols={[
              { label: "Kullanılan", key: "drinksRedeemed" },
              { label: "Satın alınan", key: "drinksPurchased" },
            ]}
          />
          <RankingCard
            title="En çok harcama yapanlar"
            icon={CreditCard}
            rows={stats.byTlSpent}
            profiles={profiles}
            formatter={(k, v) => (k.endsWith("Spent") && typeof v === "number" ? fmtTL(v) : `${v}`)}
            cols={[
              { label: "Toplam", key: "tlSpent" },
              { label: "Sipariş", key: "tlOrders" },
            ]}
          />
        </div>

        {search && (
          <section className="mt-6 rounded-2xl border border-border bg-card p-6 shadow-card">
            <h2 className="mb-3 text-lg font-semibold">Arama sonuçları ({filteredMembers.length})</h2>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
                  <tr>
                    <th className="py-2 pr-3">Üye</th>
                    <th className="py-2 px-3 text-right">Bilet</th>
                    <th className="py-2 px-3 text-right">Giriş</th>
                    <th className="py-2 px-3 text-right">Etkinlik</th>
                    <th className="py-2 px-3 text-right">J Harcanan</th>
                    <th className="py-2 px-3 text-right">İçecek</th>
                    <th className="py-2 pl-3 text-right">Harcama</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredMembers.map((p) => {
                    const agg = stats.byTickets.find((r) => r.user_id === p.id);
                    return (
                      <tr key={p.id} className="border-b border-border/50">
                        <td className="py-2 pr-3">
                          <Link
                            to="/harikaradmin/members/$userId"
                            params={{ userId: p.id }}
                            className="font-medium hover:underline"
                          >
                            {p.first_name} {p.last_name}
                          </Link>
                          <div className="text-xs text-muted-foreground">{p.email}</div>
                        </td>
                        <td className="py-2 px-3 text-right">{agg?.tickets ?? 0}</td>
                        <td className="py-2 px-3 text-right">{agg?.checkIns ?? 0}</td>
                        <td className="py-2 px-3 text-right">{agg?.eventCount ?? 0}</td>
                        <td className="py-2 px-3 text-right">{agg ? `${agg.jSpent} J` : "—"}</td>
                        <td className="py-2 px-3 text-right">{agg?.drinksRedeemed ?? 0}</td>
                        <td className="py-2 pl-3 text-right">{agg ? fmtTL(agg.tlSpent) : "—"}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}

function RankingCard({
  title,
  icon: Icon,
  rows,
  profiles,
  cols,
  formatter,
}: {
  title: string;
  icon: React.ElementType;
  rows: Array<Record<string, unknown> & { user_id: string }>;
  profiles: Record<string, ProfileLite>;
  cols: { label: string; key: string; suffix?: string }[];
  formatter?: (key: string, value: unknown) => string;
}) {
  const fmt = (key: string, value: unknown) => {
    if (formatter) return formatter(key, value);
    const suffix = cols.find((c) => c.key === key)?.suffix ?? "";
    return `${typeof value === "number" ? value.toLocaleString("tr-TR") : value}${suffix}`;
  };

  return (
    <div className="rounded-2xl border border-border bg-card p-6 shadow-card">
      <div className="mb-3 flex items-center gap-2">
        <Icon className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">{title}</h2>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="border-b border-border text-left text-xs uppercase text-muted-foreground">
            <tr>
              <th className="py-2 pr-3">#</th>
              <th className="py-2 pr-3">Üye</th>
              {cols.map((c) => (
                <th key={c.key} className="py-2 px-2 text-right">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.slice(0, 20).map((row, idx) => {
              const p = profiles[row.user_id];
              return (
                <tr key={row.user_id} className="border-b border-border/50">
                  <td className="py-2 pr-3 text-xs font-medium text-muted-foreground">{idx + 1}</td>
                  <td className="py-2 pr-3">
                    {p ? (
                      <Link
                        to="/harikaradmin/members/$userId"
                        params={{ userId: p.id }}
                        className="inline-flex items-center gap-2 hover:underline"
                      >
                        {p.photo_url ? (
                          <img
                            src={p.photo_url}
                            alt=""
                            className="h-6 w-6 rounded-full object-cover"
                            referrerPolicy="no-referrer"
                          />
                        ) : (
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-muted-foreground">
                            {p.first_name?.[0]}
                            {p.last_name?.[0]}
                          </div>
                        )}
                        <span className="font-medium">
                          {p.first_name} {p.last_name}
                        </span>
                      </Link>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </td>
                  {cols.map((c) => (
                    <td key={c.key} className="py-2 px-2 text-right font-medium">
                      {fmt(c.key, row[c.key])}
                    </td>
                  ))}
                </tr>
              );
            })}
            {rows.length === 0 && (
              <tr>
                <td colSpan={2 + cols.length} className="py-6 text-center text-muted-foreground">
                  Veri yok.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
}: {
  label: string;
  value: string;
  icon: React.ElementType;
}) {
  return (
    <div className="rounded-2xl border border-border bg-card p-4 shadow-card">
      <div className="flex items-center gap-2 text-xs uppercase tracking-wide text-muted-foreground">
        <Icon className="h-4 w-4" />
        {label}
      </div>
      <div className="mt-1 text-2xl font-bold">{value}</div>
    </div>
  );
}

function ZodiacSection({ profiles }: { profiles: Record<string, ProfileLite> }) {
  const list = Object.values(profiles);
  const counts = new Map<string, number>();
  let unknown = 0;
  for (const p of list) {
    const z = zodiacOf(p.birth_date ?? null);
    if (!z) {
      unknown++;
      continue;
    }
    counts.set(z.name, (counts.get(z.name) ?? 0) + 1);
  }
  const total = list.length - unknown;
  const rows = ZODIAC_ORDER.map((name) => ({ name, count: counts.get(name) ?? 0 })).sort(
    (a, b) => b.count - a.count,
  );
  const max = Math.max(1, ...rows.map((r) => r.count));

  return (
    <section className="mb-6 rounded-2xl border border-border bg-card p-6 shadow-card">
      <h2 className="mb-1 text-lg font-semibold">Burç dağılımı</h2>
      <p className="mb-4 text-xs text-muted-foreground">
        {total.toLocaleString("tr-TR")} üye · {unknown.toLocaleString("tr-TR")} üyede doğum tarihi yok
      </p>
      <div className="grid gap-2 sm:grid-cols-2">
        {rows.map((r) => (
          <div key={r.name} className="flex items-center gap-3">
            <span className="w-24 shrink-0 text-sm">{r.name}</span>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-muted">
              <div className="h-full rounded-full bg-primary" style={{ width: `${(r.count / max) * 100}%` }} />
            </div>
            <span className="w-20 shrink-0 text-right text-sm font-medium">
              {r.count.toLocaleString("tr-TR")}
              <span className="ml-1 text-xs text-muted-foreground">
                {total > 0 ? `${Math.round((r.count / total) * 100)}%` : "0%"}
              </span>
            </span>
          </div>
        ))}
      </div>
    </section>
  );
}
